import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DetailCard } from '../../../../components/ui/DetailCard';
import { SyncStatusIcon } from '../../../../components/commons/syncStatus/SyncStatusIcon';
import { TerrainChips } from '../TerrainChips';
import type { InstanceRow, Terrain } from '../../instanceTypes';

export interface InstanceInfoEditState {
  name: string;
  email: string;
  errors: { name?: string | null; email?: string | null };
  touched: Record<string, boolean>;
  onChange: (field: 'name' | 'email', value: string) => void;
  onBlur: (field: string) => void;
}

interface InstanceInfoSectionProps {
  instance: InstanceRow;
  editState: InstanceInfoEditState;
  /** Name and email rendered as plain text, not inputs (ADR-040 D5). */
  readOnly?: boolean;
  terrainMap?: Map<string, Terrain>;
}

function ReadOnlyField({ label, value }: { label: string; value: string | null }) {
  const theme = useTheme();
  return (
    <Box component="dl" sx={{ m: 0 }}>
      <Typography component="dt" variant="body2" sx={{ color: theme.palette.text.label, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ color: theme.palette.text.body, m: 0 }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

export function InstanceInfoSection({
  instance,
  editState,
  readOnly = false,
  terrainMap,
}: InstanceInfoSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation('instances');
  const { name, email, errors, touched, onChange, onBlur } = editState;

  return (
    <DetailCard
      strip={theme.palette.primary.main}
      iconBg={theme.palette.primary.main}
      icon={<InfoOutlinedIcon aria-hidden="true" sx={{ fontSize: theme.iconSize.sm }} />}
      title={t('detail.section.info')}
      subtitle={t('detail.section.infoSubtitle')}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 3.5, columnGap: 2.5 }}>
        {/* Row 1 — Status | Identifiant technique */}
        <Box component="dl" sx={{ m: 0 }}>
          <Typography
            component="dt"
            variant="body2"
            sx={{ color: theme.palette.text.label, mb: 0.5 }}
          >
            {t('detail.fields.status')}
          </Typography>
          <Box component="dd" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, m: 0 }}>
            <SyncStatusIcon status={instance.status} fontSize="medium" />
            <Typography variant="body2" sx={{ color: theme.palette.text.body }}>
              {t(`status.${instance.status}`)}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.label }}>
              {t('detail.fields.guid')}
            </Typography>
            <Tooltip title={t('fields.technicalId.tooltip')} placement="top">
              <InfoOutlinedIcon
                aria-hidden="true"
                sx={{
                  fontSize: theme.iconSize.xs,
                  color: theme.palette.text.label,
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.body, wordBreak: 'break-all' }}
          >
            {instance.id}
          </Typography>
        </Box>

        {/* Row 2 — Name | Email */}
        {readOnly ? (
          <>
            <ReadOnlyField label={t('detail.fields.name')} value={instance.name} />
            <ReadOnlyField label={t('detail.fields.email')} value={instance.email} />
          </>
        ) : (
          <>
            <TextField
              label={t('detail.fields.name')}
              size="small"
              value={name}
              onChange={e => onChange('name', e.target.value)}
              onBlur={() => onBlur('name')}
              error={!!(touched.name && errors.name)}
              helperText={touched.name ? errors.name : undefined}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />

            <TextField
              label={t('detail.fields.email')}
              size="small"
              required
              type="email"
              value={email}
              onChange={e => onChange('email', e.target.value)}
              onBlur={() => onBlur('email')}
              error={!!(touched.email && errors.email)}
              helperText={touched.email ? errors.email : undefined}
            />
          </>
        )}

        {/* Row 3 — PC Name | Install path */}
        <ReadOnlyField label={t('detail.fields.pcName')} value={instance.computerName} />
        <ReadOnlyField label={t('detail.fields.path')} value={instance.path} />

        {/* Row 4 — Country | Linked fields */}
        <ReadOnlyField label={t('detail.fields.pays')} value={instance.pays} />

        {instance.codeterrain.length > 0 ? (
          <Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.label, mb: 0.5 }}>
              {t('detail.fields.terrains')}
            </Typography>
            <TerrainChips codes={instance.codeterrain} terrainMap={terrainMap} />
          </Box>
        ) : (
          <Box aria-hidden="true" />
        )}

        {/* Row 5 — Version | GEN version */}
        <ReadOnlyField label={t('detail.fields.version')} value={instance.version} />
        <ReadOnlyField label={t('detail.fields.genVersion')} value={instance.genVersion} />
      </Box>
    </DetailCard>
  );
}
